import React, { useState } from 'react';
import { MaintenanceRecord, MaintenanceFormData, MaintenanceType, MaintenanceStatus } from '../../types/maintenance.ts';

interface MaintenanceFormProps {
  propertyId: string;
  record?: MaintenanceRecord | null;
  onSubmit: (data: MaintenanceFormData) => Promise<void>;
  onClose: () => void;
}

const MaintenanceForm: React.FC<MaintenanceFormProps> = ({ propertyId, record, onSubmit, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<MaintenanceFormData>({
    property_id: propertyId,
    maintenance_type: record?.maintenance_type || 'Regular',
    description: record?.description || '',
    cost: record?.cost || 0,
    start_date: record?.start_date || new Date().toISOString().split('T')[0],
    end_date: record?.end_date || undefined, // Use undefined instead of null
    status: record?.status || 'Pending',
    vendor_details: record?.vendor_details || '',
    notes: record?.notes || ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (formData.cost <= 0) {
      errors.cost = 'Please enter a maintenance cost';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Only include end_date if it has a value
      const dataToSubmit = {
        ...formData,
        end_date: formData.end_date || undefined // Ensure undefined instead of null
      };

      await onSubmit(dataToSubmit);
      onClose();
    } catch (error) {
      console.error('Error submitting maintenance record:', error);
      alert('Failed to save maintenance record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal" onClick={onClose}>
      <div className="maintenance-form" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{record ? 'Edit' : 'Add'} Maintenance Record</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Type *</label>
              <select
                value={formData.maintenance_type}
                onChange={(e) => setFormData({
                  ...formData,
                  maintenance_type: e.target.value as MaintenanceType
                })}
                required
              >
                <option value="Regular">Regular</option>
                <option value="Repair">Repair</option>
                <option value="Renovation">Renovation</option>
                <option value="Emergency">Emergency</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({
                  ...formData,
                  status: e.target.value as MaintenanceStatus
                })}
                required
              >
                <option value="Pending">Pending</option>
                <option value="In-Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group">
              <label>Cost (₹) *</label>
              <input
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({
                  ...formData,
                  cost: parseFloat(e.target.value) || 0
                })}
                required
                min="0"
              />
              {validationErrors.cost && (
                <span className="validation-message">{validationErrors.cost}</span>
              )}
            </div>

            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({
                  ...formData,
                  start_date: e.target.value
                })}
                required
              />
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  end_date: e.target.value || undefined  // Changed null to undefined
                })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({
                ...formData,
                description: e.target.value
              })}
              required
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Vendor Details</label>
            <input
              type="text"
              value={formData.vendor_details}
              onChange={(e) => setFormData({
                ...formData,
                vendor_details: e.target.value
              })}
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({
                ...formData,
                notes: e.target.value
              })}
              rows={2}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (record ? 'Update' : 'Add')} Record
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaintenanceForm;
